import { $logger } from '@/boot/logger'
import { $rollbar } from '@/boot/rollbar'
import { $pegApi } from '@/boot/peg-api'
import { useQueryClient } from '@tanstack/vue-query'

/**
 * Store for public retailer data (customer-facing)
 * Uses Vue Query for caching and data fetching
 * Separate from admin retailer store to keep concerns separated
 */
export const useRetailerPublicStore = defineStore('retailerPublic', () => {
  const queryClient = useQueryClient()

  /**
   * Fetch and cache public retailer data
   * Used when you need to fetch data imperatively (e.g., in a store or utility function)
   * For components, prefer using the useRetailerPublicData composable instead
   * @param {String} retailerId
   * @returns {Promise<Object>}
   */
  async function fetchPublicRetailer(retailerId) {
    if (!retailerId) {
      $logger.error('fetchPublicRetailer: retailerId is required')
      return { success: false, error: 'Retailer ID is required' }
    }

    try {
      const result = await queryClient.fetchQuery({
        queryKey: ['retailer-public', retailerId],
        queryFn: () => $pegApi.Retailers.getPublic(retailerId),
      })

      return result
    } catch (error) {
      $rollbar.error('Error fetching public retailer data', error)
      throw error
    }
  }

  /**
   * Clear cached retailer data (useful for testing or forced refresh)
   * @param {String} retailerId - Optional. If not provided, clears all retailer-public cache
   */
  function clearCache(retailerId = null) {
    if (retailerId) {
      queryClient.removeQueries({ queryKey: ['retailer-public', retailerId] })
    } else {
      queryClient.removeQueries({ queryKey: ['retailer-public'] })
    }
  }

  /**
   * Invalidate cached retailer data (marks as stale, will refetch on next use)
   * @param {String} retailerId - Optional. If not provided, invalidates all retailer-public cache
   */
  function invalidateCache(retailerId = null) {
    if (retailerId) {
      queryClient.invalidateQueries({ queryKey: ['retailer-public', retailerId] })
    } else {
      queryClient.invalidateQueries({ queryKey: ['retailer-public'] })
    }
  }

  return {
    // ACTIONS
    fetchPublicRetailer,
    clearCache,
    invalidateCache,
  }
})
